import React, { Component } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-analytics-web-react/src/utils/fire-track-event.tsx';
import { createServerSideValidationErrorsGetter } from '@atlassian/jira-common-directory-v2/src/utils/shareable-entity-dialog';
import log from '@atlassian/jira-common-util-logging/src/log';
import copyFilterErrorFlag from '@atlassian/jira-copy-filters-modal/src/common/ui/flags/error/index.tsx';
import copyFilterSuccessFlag from '@atlassian/jira-copy-filters-modal/src/common/ui/flags/success/index.tsx';
import { VALIDATION_ERROR_NAME } from '@atlassian/jira-fetch/src/utils/errors';
import {
	fireUIAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { SharingPermissionAwareShareableEntityModal } from '@atlassian/jira-shareable-entity-dialog/src';
import {
	ENTITY_NAME_FIELD_NAME,
	SHARE_PERMISSIONS_FIELD_NAME,
	EDIT_PERMISSIONS_FIELD_NAME,
} from '@atlassian/jira-shareable-entity-dialog/src/model/constants.tsx';
import type {
	OperationData,
	OperationResult,
} from '@atlassian/jira-shareable-entity-dialog/src/model/types.tsx';
import type { ContentProps } from '../../common/types';
import createFilter from '../../services/create-filter';
import messages from './messages';

const getServerSideValidationErrors = createServerSideValidationErrorsGetter({
	filterName: ENTITY_NAME_FIELD_NAME,
	shares: SHARE_PERMISSIONS_FIELD_NAME,
	edit_permissions: EDIT_PERMISSIONS_FIELD_NAME,
});

// eslint-disable-next-line jira/react/no-class-components
class CopyModal extends Component<ContentProps> {
	componentDidMount() {
		this.props.onInitiateSuccess();
	}

	onOperation = (filter: OperationData) => {
		const {
			isAdmin,
			filter: { favourite, jql },
		} = this.props;

		return createFilter(filter, jql, favourite, isAdmin);
	};

	onOperationSucceed = (
		filter: OperationData,
		result: OperationResult<{ id: string; favourite: boolean }>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const { flagService, onCopied, id } = this.props;
		fireUIAnalytics(analyticsEvent, 'copiedFilter', {
			isViewerPrivate: filter.sharePermissions.length === 0,
			isEditorPrivate: filter.editPermissions && filter.editPermissions.length === 0,
		});
		flagService.showFlag(copyFilterSuccessFlag(filter.name));

		// Emit an event to notify consumers that there is a newly favourited entity
		if (result.favourite) {
			this.props.emitChangedFavorite({
				id: result?.id,
				type: 'filters',
				value: result.favourite,
			});
		}

		onCopied();

		fireTrackAnalytics(analyticsEvent, {
			action: 'copied',
			actionSubjectId: `${id}`,
		});
		this.props.onCloseCopyFilterModal();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onOperationFailed = (error: any, analyticsEvent: UIAnalyticsEvent) => {
		const { flagService, id } = this.props;
		const isValidationError = error.name && error.name === VALIDATION_ERROR_NAME;

		if (!isValidationError) {
			log.safeErrorWithoutCustomerData(
				'directories.filters.copy-modal.submit',
				`copy filter has failed, id=${id}`,
				error,
			);
		}

		flagService.showFlag(copyFilterErrorFlag());

		fireOperationalAnalytics(analyticsEvent, 'copy failed', id, {
			isValidationError,
		});
	};

	onCancel = () => {
		this.props.onCancel();
		this.props.onCloseCopyFilterModal();
	};

	render() {
		const {
			intl: { formatMessage },
		} = this.props;
		const { name, description, editPermissions, sharePermissions } = this.props.filter;

		return (
			<>
				<SharingPermissionAwareShareableEntityModal
					sharingPermissionType="filter"
					avoidInitialPublicSharePermission
					sharePermissions={sharePermissions}
					editPermissions={editPermissions}
					title={formatMessage(messages.header, { name })}
					actionButtonCaption={formatMessage(messages.saveButtonCaption)}
					initialName={name}
					initialDescription={description}
					initialOwner={undefined}
					onCancel={this.onCancel}
					onOperationSucceed={this.onOperationSucceed}
					onOperationFailed={this.onOperationFailed}
					getServerSideValidationErrors={getServerSideValidationErrors}
					operation={this.onOperation}
				/>
				<ViewTracker />
			</>
		);
	}
}

export default CopyModal;
